import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';

const TYPE_LABELS = {
  long_course: 'Long Course',
  block: 'Block Course',
  workshop: 'Workshop'
};

const TZ_SHORT = {
  'America/New_York': 'ET',
  'America/Chicago': 'CT',
  'America/Denver': 'MT',
  'America/Los_Angeles': 'PT',
  'Asia/Kolkata': 'IST'
};

const LOCATION_LABELS = {
  online: 'Online',
  in_person: 'In-Person',
  hybrid: 'Hybrid'
};

const STATUS_CONFIG = {
  active: { label: 'Active', bg: 'bg-green-100', text: 'text-green-700' },
  completed: { label: 'Completed', bg: 'bg-blue-100', text: 'text-blue-700' },
  cancelled: { label: 'Cancelled', bg: 'bg-gray-100', text: 'text-gray-700' },
  payment_failed: { label: 'Payment Failed', bg: 'bg-red-100', text: 'text-red-700' },
  pending_payment: { label: 'Pending Payment', bg: 'bg-yellow-100', text: 'text-yellow-700' }
};

const formatSchedule = (course) => {
  if (!course?.schedule) return null;

  const tz = TZ_SHORT[course.schedule.timezone] || course.schedule.timezone || '';
  const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1, 3) : '';

  if (course.type === 'workshop') {
    if (course.schedule.sessionDate) {
      const d = new Date(course.schedule.sessionDate);
      const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      return course.schedule.startTime ? `${dateStr} · ${course.schedule.startTime} ${tz}`.trim() : dateStr;
    }
    return null;
  }

  if (course.type === 'block') {
    const parts = [];
    if (course.schedule.blockStartDate) {
      const d = new Date(course.schedule.blockStartDate);
      parts.push(`Starts ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`);
    }
    if (course.schedule.blockWeeks) {
      parts.push(`${course.schedule.blockWeeks} weeks`);
    }
    if (course.schedule.days?.length > 0) {
      parts.push(course.schedule.days.map(capitalize).join(', '));
    }
    if (course.schedule.startTime) {
      parts.push(`${course.schedule.startTime} ${tz}`.trim());
    }
    return parts.join(' · ');
  }

  // long_course
  if (course.schedule.days?.length > 0) {
    const days = course.schedule.days.map(capitalize).join(', ');
    const time = course.schedule.startTime
      ? `${course.schedule.startTime}${course.schedule.endTime ? ' - ' + course.schedule.endTime : ''} ${tz}`.trim()
      : '';
    return time ? `${days} · ${time}` : days;
  }

  return null;
};

const CourseDetail = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [enrollment, setEnrollment] = useState(null);
  const [materials, setMaterials] = useState(null);
  const [loading, setLoading] = useState(true);
  const [materialsLoading, setMaterialsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch enrollments to find this course
        const { data } = await api.get('/enroll/my');
        const found = (data.enrollments || []).find(
          (e) => e.course?._id === courseId
        );
        if (found) {
          setEnrollment(found);
        } else {
          setError('Enrollment not found for this course.');
        }
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load course details');
      } finally {
        setLoading(false);
      }
    };

    const fetchMaterials = async () => {
      try {
        const { data } = await api.get(`/materials/student/${courseId}`);
        setMaterials(data);
      } catch (err) {
        // Materials may not exist for this course — that's okay
        setMaterials({ materials: [], attendanceCount: 0, totalWeeks: 0 });
      } finally {
        setMaterialsLoading(false);
      }
    };

    fetchData();
    fetchMaterials();
  }, [courseId]);

  if (loading) {
    return <div className="text-gray-500">Loading course details...</div>;
  }

  if (error) {
    return (
      <div>
        <button
          onClick={() => navigate('/student/courses')}
          className="text-sm text-gray-500 hover:text-gray-700 mb-4 inline-flex items-center gap-1"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
          </svg>
          My Courses
        </button>
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      </div>
    );
  }

  const course = enrollment.course;
  const status = STATUS_CONFIG[enrollment.status] || STATUS_CONFIG.active;
  const schedule = formatSchedule(course);
  const hasZoom = course.zoom?.joinUrl;
  const hasClassroom = course.googleClassroom?.courseUrl;
  const totalAttended = enrollment.totalClassesAttended || 0;
  const totalMissed = enrollment.totalClassesMissed || 0;
  const totalClasses = totalAttended + totalMissed;
  const attendanceRate = totalClasses > 0 ? Math.round((totalAttended / totalClasses) * 100) : 0;

  const unlockedCount = materials?.materials?.filter((m) => m.unlocked).length || 0;
  const totalWeeks = materials?.totalWeeks || materials?.materials?.length || 0;

  return (
    <div>
      {/* Header */}
      <button
        onClick={() => navigate('/student/courses')}
        className="text-sm text-gray-500 hover:text-gray-700 mb-4 inline-flex items-center gap-1"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
        </svg>
        My Courses
      </button>

      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{course.name}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {course.danceStyle || ''}
            {course.danceStyle && course.levelName ? ' · ' : ''}
            {course.levelName || ''}
            {(course.danceStyle || course.levelName) && course.type ? ' · ' : ''}
            {TYPE_LABELS[course.type] || ''}
          </p>
        </div>
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.bg} ${status.text}`}>
          {status.label}
        </span>
      </div>

      {/* Quick Info Bar */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 mb-6">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-gray-600">
          {schedule && (
            <span className="flex items-center gap-1.5">
              <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
              </svg>
              {schedule}
            </span>
          )}

          <span className="flex items-center gap-1.5">
            <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
            </svg>
            {LOCATION_LABELS[course.location?.type] || 'In-Person'}
            {course.location?.address && (
              <span className="text-gray-400 ml-1">({course.location.address})</span>
            )}
          </span>

          {hasZoom && (
            <a
              href={course.zoom.joinUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 bg-brand-500 text-white px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-brand-600 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" />
              </svg>
              Join Class
            </a>
          )}

          {hasClassroom && (
            <a
              href={course.googleClassroom.courseUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-brand-500 hover:text-brand-600 font-medium"
            >
              View in Classroom
            </a>
          )}
        </div>
      </div>

      {/* Attendance Summary */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Attendance</h2>
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
            <div className="text-3xl font-bold text-gray-900">{totalAttended}</div>
            <div className="text-sm text-gray-500 mt-1">Classes Attended</div>
          </div>
          <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
            <div className="text-3xl font-bold text-gray-900">{totalMissed}</div>
            <div className="text-sm text-gray-500 mt-1">Classes Missed</div>
          </div>
          <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
            <div className="text-3xl font-bold text-brand-500">{attendanceRate}%</div>
            <div className="text-sm text-gray-500 mt-1">Attendance Rate</div>
          </div>
        </div>
      </div>

      {/* Materials Section */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900">Class Materials</h2>
          {!materialsLoading && totalWeeks > 0 && (
            <span className="text-sm text-gray-500">
              {unlockedCount} of {totalWeeks} weeks unlocked
            </span>
          )}
        </div>

        {materialsLoading ? (
          <div className="text-gray-500 text-sm">Loading materials...</div>
        ) : !materials?.materials?.length ? (
          <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm text-center">
            <p className="text-gray-500 text-sm">No materials have been added for this course yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Progress bar */}
            {totalWeeks > 0 && (
              <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
                <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                  <span>Progress</span>
                  <span className="font-medium">{Math.round((unlockedCount / totalWeeks) * 100)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-brand-500 rounded-full h-2 transition-all"
                    style={{ width: `${(unlockedCount / totalWeeks) * 100}%` }}
                  />
                </div>
              </div>
            )}

            {materials.materials.map((week) => (
              <div
                key={week.week}
                className={`bg-white rounded-xl border shadow-sm overflow-hidden ${
                  week.unlocked ? 'border-gray-100' : 'border-gray-100 opacity-60'
                }`}
              >
                <div className="p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`px-2.5 py-1 rounded-lg text-sm font-bold ${
                      week.unlocked
                        ? 'bg-brand-100 text-brand-600'
                        : 'bg-gray-100 text-gray-400'
                    }`}>
                      Week {week.week}
                    </span>
                    {week.unlocked ? (
                      <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 10.5V6.75a4.5 4.5 0 119 0v3.75M3.75 21.75h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H3.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                      </svg>
                    )}
                    <span className={`text-sm ${week.unlocked ? 'text-gray-700 font-medium' : 'text-gray-400'}`}>
                      {week.title || `Week ${week.week}`}
                    </span>
                  </div>

                  {week.unlocked ? (
                    <div className="space-y-4 mt-3">
                      {week.videos?.map((video, vi) => (
                        <div key={vi}>
                          <p className="text-sm font-medium text-gray-700 mb-2">{video.title || `Video ${vi + 1}`}</p>
                          {video.embedUrl && (video.type === 'youtube' || video.type === 'gdrive') ? (
                            <div className="relative" style={{ paddingBottom: '56.25%' }}>
                              <iframe
                                src={video.embedUrl}
                                title={video.title || `Video ${vi + 1}`}
                                className="absolute inset-0 w-full h-full rounded-lg"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                              />
                              {/* Watermark overlay — covers top bar to block Share button */}
                              <div className="absolute top-0 left-0 right-0 h-12 pointer-events-auto z-10" />
                              <div className="absolute top-2 right-2 text-xs text-white/60 bg-black/20 px-2 py-0.5 rounded pointer-events-none select-none z-20">
                                {user?.name} &middot; {user?.email}
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
                              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                                <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                              </div>
                              <p className="text-sm font-medium text-gray-700">{video.title || `Video ${vi + 1}`}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 ml-4 mt-1">
                      This week's materials will be available soon
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Billing Info */}
      {enrollment.billingPlan && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Billing</h2>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
              <div>
                <div className="text-sm text-gray-500">Plan</div>
                <div className="font-medium text-gray-900">{enrollment.billingPlan.name || 'Standard'}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Amount</div>
                <div className="font-medium text-gray-900">
                  ${enrollment.billingPlan.amount}
                  {enrollment.billingPlan.frequency && enrollment.billingPlan.frequency !== 'one_time' && enrollment.billingPlan.frequency !== 'one-time'
                    ? `/${enrollment.billingPlan.frequency}`
                    : ' one-time'}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Status</div>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${status.bg} ${status.text}`}>
                  {status.label}
                </span>
              </div>
              <div>
                <div className="text-sm text-gray-500">Enrolled</div>
                <div className="font-medium text-gray-900">
                  {enrollment.enrolledAt
                    ? new Date(enrollment.enrolledAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : '--'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseDetail;
